import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiUser, FiMessageSquare, FiCheckCircle } = FiIcons;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you for your inquiry. We will contact you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      company: '',
      service: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone',
      details: ['+27 11 123 4567', '+27 16 987 6543'],
      description: 'Call us for immediate assistance'
    },
    {
      icon: FiMail,
      title: 'Email',
      details: ['info@gautengmanufacturing.co.za', 'quotes@gautengmanufacturing.co.za'],
      description: 'Send us your detailed requirements'
    },
    {
      icon: FiMapPin,
      title: 'Locations',
      details: ['Germiston Facility', 'Vanderbijlpark Facility'],
      description: 'Visit our state-of-the-art facilities'
    },
    {
      icon: FiClock,
      title: 'Business Hours',
      details: ['Mon-Fri: 6:00 AM - 6:00 PM', 'Sat: 8:00 AM - 2:00 PM'],
      description: '24/7 emergency support available'
    }
  ];

  const services = [
    'Heavy Engineering & Fabrication',
    'Precision Machining & CNC Operations',
    'Industrial Automation & Control Systems',
    'Quality Assurance & Testing Services',
    'Custom Component Manufacturing',
    'Maintenance & Refurbishment Services'
  ];

  const facilities = [
    {
      name: 'Germiston Facility',
      address: '123 Industrial Avenue, Germiston, 1401',
      specialties: ['Heavy Fabrication', 'Structural Steelwork', 'Pressure Vessels'],
      size: '8 hectares'
    },
    {
      name: 'Vanderbijlpark Facility',
      address: '456 Manufacturing Street, Vanderbijlpark, 1911',
      specialties: ['Precision Machining', 'CNC Operations', 'Quality Testing'],
      size: '7 hectares'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto text-gray-200">
              Get in touch with our engineering team to discuss your manufacturing requirements 
              and receive expert consultation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={info.icon} className="text-2xl text-blue-800" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{info.title}</h3>
                <div className="space-y-1 mb-3">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-700 font-medium">{detail}</p>
                  ))}
                </div>
                <p className="text-sm text-gray-600">{info.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Contact Form and Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Request a Quote</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiUser} className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="your.email@company.com"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Required
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Details *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-3 text-gray-400" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Please describe your project requirements, specifications, and timeline..."
                    ></textarea>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-800 hover:bg-blue-900 text-white py-3 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiSend} />
                  <span>Send Inquiry</span>
                </button>
              </form>
            </motion.div>

            {/* Additional Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Choose Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="mt-1" style={{ color: '#FA7315' }} />
                    <span className="text-gray-700">24-hour response time for all inquiries</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="mt-1" style={{ color: '#FA7315' }} />
                    <span className="text-gray-700">Free consultation and project assessment</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="mt-1" style={{ color: '#FA7315' }} />
                    <span className="text-gray-700">Detailed technical proposals with CAD drawings</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="mt-1" style={{ color: '#FA7315' }} />
                    <span className="text-gray-700">Competitive pricing with transparent quotes</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Emergency Services</h4>
                <p className="text-gray-700 mb-3">
                  For urgent manufacturing requirements or equipment breakdowns, 
                  contact our emergency hotline:
                </p>
                <div className="text-2xl font-bold text-orange-500">+27 82 123 4567</div>
                <p className="text-sm text-gray-600 mt-2">Available 24/7 for critical situations</p>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Quality Guarantee</h4>
                <p className="text-gray-700">
                  All our work is backed by comprehensive warranties and quality guarantees. 
                  We stand behind every component we manufacture with our ISO 9001:2015 
                  certified quality management system.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Facilities Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Facilities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Visit our world-class manufacturing facilities equipped with the latest technology 
              and operated by skilled professionals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {facilities.map((facility, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-xl p-8 shadow-lg"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{facility.name}</h3>
                <div className="flex items-start space-x-2 mb-4">
                  <SafeIcon icon={FiMapPin} className="text-orange-500 mt-1" />
                  <span className="text-gray-700">{facility.address}</span>
                </div>
                <div className="mb-4">
                  <span className="text-sm font-medium text-gray-500">Facility Size: </span>
                  <span className="text-gray-700">{facility.size}</span>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Specialties:</h4>
                  <div className="flex flex-wrap gap-2">
                    {facility.specialties.map((specialty, idx) => (
                      <span
                        key={idx}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;